import type { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataGridPagination } from '~/components/data-grid';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams } from '~/hooks/pagination';
import { useDeviceMetricColumns, useEventMetricColumns, zoneDisplayLabel } from '../lib';
import type { PlayoutReport__AssetSummary } from '../queries/PlayoutReportByAsset.generated';
import { AssetLink } from './AssetLink';
import { ReportDataGrid } from './ReportDataGrid';

export type AssetSummaryRow = PlayoutReport__AssetSummary;

export interface AssetSummaryTableProps {
  data: ReadonlyArray<AssetSummaryRow>;
  emptyLabel: string;
  columnStateKey: string;
}

export const AssetSummaryTable = ({ data, emptyLabel, columnStateKey }: AssetSummaryTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();

  const { sortModel, setOrderByParams } = useOrderByParams();
  const { paginationModel, setPageParams } = usePaginationParams();

  const eventMetricColumns = useEventMetricColumns<AssetSummaryRow>();
  const deviceColumns = useDeviceMetricColumns<AssetSummaryRow>();

  const columns = useMemo<GridColDef<AssetSummaryRow>[]>(
    () => [
      {
        field: 'asset',
        headerName: t('pages.playout_reports.top_assets.columns.asset'),
        flex: 1.5,
        minWidth: 180,
        hideable: false,
        renderCell: ({ row }) => (
          <AssetLink
            kind={row.kind}
            mediaItemId={row.mediaItemId ?? null}
            contentItemId={row.contentItemId ?? null}
            contentFolderId={row.contentFolderId ?? null}
            label={row.asset}
          />
        ),
      },
      {
        field: 'zones',
        headerName: t('pages.playout_reports.top_assets.columns.asset_type'),
        flex: 1,
        valueGetter: (_value, row) => row.zones.map((z) => zoneDisplayLabel(z)).join(', '),
      },
      ...eventMetricColumns,
      ...deviceColumns,
    ],
    [t, eventMetricColumns, deviceColumns],
  );

  return (
    <ReportDataGrid
      columnStateKey={columnStateKey}
      columns={columns}
      rows={data}
      disableRowSelectionOnClick
      // Row key mirrors the service's GROUP BY (asset_id, kind); rows whose
      // asset has no id fall back to the display name.
      getRowId={(row: AssetSummaryRow) =>
        row.assetId != null ? `${row.kind}::${row.assetId}` : `${row.kind}::name::${row.asset}`
      }
      onRowClick={({ row }: GridRowParams<AssetSummaryRow>) => {
        if (row.assetId == null) return;
        navigate({ pathname: `../by-asset/${row.kind}/${row.assetId}`, search });
      }}
      pageSizeOptions={[25, 50, 100]}
      sortModel={sortModel}
      onSortModelChange={(m) => setOrderByParams(m.at(0) ?? null)}
      paginationModel={paginationModel}
      onPaginationModelChange={(m) => setPageParams({ page: m.page + 1, perPage: m.pageSize })}
      slots={{ pagination: DataGridPagination }}
      localeText={{ noRowsLabel: emptyLabel }}
    />
  );
};
